import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DesktopOnlyNotice,
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
  LifeReadinessSubcategory,
  QuestionView,
  Questionnaire,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useUpdateQuestionUserLevelMutation } from '@backrs_ui/grow-ui/src/redux/lifeReadiness/lifeReadiness.api';

import { Box, useMediaQuery, Theme } from '@mui/material';

import { ExplainerCard } from './molecules/ExplainerCard';
import { getPaginationProps } from './utils/handleLifeReadinessPagination';
import {
  getTooltips,
  LEVEL_MAP,
  getProgressTabsProps,
  getQuestionsForSubcategory,
} from './utils/lifeReadinessQuestionnaire.util';

type LifeReadinessQuestionnaireProps = Readonly<{
  categories: LifeReadinessCategory[];
  subcategories: LifeReadinessSubcategory[];
  questions: LifeReadinessQuestion[];
  userLevels: LifeReadinessQuestionUserLevel[];
  foundCategory: LifeReadinessCategory;
  foundSubcategory: LifeReadinessSubcategory;

  showExplainerCard?: boolean;
}>;

export const LifeReadinessQuestionnaire = ({
  categories,
  subcategories,
  questions,
  userLevels,
  foundCategory,
  foundSubcategory,
  showExplainerCard = false,
}: LifeReadinessQuestionnaireProps) => {
  const navigate = useNavigate();
  const [updateQuestionUserLevel] = useUpdateQuestionUserLevelMutation();
  const { user } = useAppSelector((state) => state.user);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const userLevelMap = useMemo(() => {
    return Object.fromEntries(
      (userLevels ?? []).map((level) => [level.life_survey_question_id, Number(level.level)]),
    );
  }, [userLevels]);

  const handleAnswerChange = async (question: LifeReadinessQuestion, key: string) => {
    const level = LEVEL_MAP[key];

    if (level && user?.id) {
      try {
        await updateQuestionUserLevel({
          user_id: user.id,
          life_survey_question_id: question.id,
          level,
        });
      } catch (e) {
        console.error('Failed to update user level', e);
      }
    }
  };

  const handleSubcategoryClick = (tabId: string, subcategoryId: string) => {
    const clickedCategory = categories.find((c) => c.name === tabId);

    if (clickedCategory) {
      navigate(`${APP_ROUTE.LIFE_READINESS}/${clickedCategory.slug}/${subcategoryId}`);
    }
  };

  const paginationProps = getPaginationProps(
    categories,
    subcategories,
    foundCategory,
    foundSubcategory,
    showExplainerCard,
    navigate,
  );

  const progressTabs = getProgressTabsProps(
    categories,
    subcategories,
    questions,
    userLevelMap,
    foundCategory,
  );

  if (isMobile) {
    return <DesktopOnlyNotice />;
  }

  return (
    <Box margin="0 auto" width="100%">
      <Questionnaire
        tabs={progressTabs}
        activeTabId={foundCategory?.name}
        activeSubcategoryId={foundSubcategory?.name.toLowerCase()}
        onSubcategoryClick={handleSubcategoryClick}
        paginationProps={{
          ...paginationProps,
          totalPages: paginationProps.totalPages + 1, // +1 for the explainer card
          currentPage: showExplainerCard ? 1 : paginationProps.currentPage + 1,
          finishButtonOnClick: () => {
            navigate(APP_ROUTE.LIFE_READINESS);
          },
          backButtonOnClick: () => {
            // if we're on the explainer card, navigate to the dashboard
            if (showExplainerCard) {
              navigate(APP_ROUTE.LIFE_READINESS);

              return;
            }

            // if we're on the first subcategory of the first category, navigate to the explainer card
            navigate(`${APP_ROUTE.LIFE_READINESS}/quiz`);
          },
        }}
        showSubcategories={!showExplainerCard}
      >
        {showExplainerCard ? (
          <ExplainerCard />
        ) : (
          <QuestionView
            header={{
              title: foundCategory.name,
              description: foundCategory.description,
              subCategory: foundSubcategory.name,
              tooltips: getTooltips(foundSubcategory.name),
            }}
            questions={getQuestionsForSubcategory(
              questions,
              foundSubcategory.id,
              userLevelMap,
              handleAnswerChange,
            )}
          />
        )}
      </Questionnaire>
    </Box>
  );
};
